import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import AuthPage from "./AuthPage";
import BrandIcon from "./BrandIcon";
import LocalAccountGate from "./LocalAccountGate";
import { localAccount } from "./lib/localStore/accountScope";
import "./landing.css";
import BetaFeedback from "./BetaFeedback";
import DesktopViewportGate from "./DesktopViewportGate";
import DriveSyncWorkspace, { DriveOAuthComplete } from "./DriveSyncWorkspace";
import { i18n, useTranslation } from "./i18n";
import LegalPage from "./LegalPage";
import { useAuthSession } from "./lib/data";
import { syncReviewId } from "./lib/driveSync/reviewScope";
import { DRIVE_SYNC_INVITE, DRIVE_SYNC_RELEASED, QUIZ_RELEASED } from "./lib/features";
import { useBetaAccess } from "./lib/useBetaAccess";
import MarketingSite from "./MarketingSite";
import PwaUpdateNotice from "./PwaUpdateNotice";
import { notifyPwaAccessRestored, PWA_CHECK_EVENT } from "./pwa";
import StudentQuiz from "./StudentQuiz";
import SupportPage from "./SupportPage";
import TeacherConsole from "./TeacherConsole";

const SyncReviewApp = lazy(() => import("./SyncReviewApp"));

function CloudApp() {
  const legacyPreview = new URLSearchParams(location.search).has("preview");

  return (
    <Routes>
      <Route path="/update-check" element={<PwaAccessReturn />} />
      <Route path="/about" element={<MarketingSite />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/data-processing" element={<LegalPage kind="data-processing" />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/join/:code" element={QUIZ_RELEASED ? <StudentQuiz /> : <Navigate to="/" replace />} />
      <Route path="/settings/*" element={<Navigate to="/" replace />} />
      <Route path="/paid/*" element={<Navigate to="/" replace />} />
      <Route
        path="/*"
        element={
          legacyPreview ? (
            <DesktopViewportGate>
              <TeacherConsole
                user={null}
                preview
                onExitPreview={() => {
                  location.href = "/";
                }}
                onSignOut={async () => {
                  location.href = "/";
                }}
              />
            </DesktopViewportGate>
          ) : (
            <MarketingSite />
          )
        }
      />
    </Routes>
  );
}

function PwaAccessReturn() {
  const { t } = useTranslation("pwaInstall");
  useEffect(notifyPwaAccessRestored, []);
  return (
    <main className="loading-screen">
      <BrandIcon />
      <h1>{t("accessRestored")}</h1>
      <p>{t("returnToUpdate")}</p>
      <button type="button" onClick={() => window.close()}>
        {t("returnToWorkspace")}
      </button>
    </main>
  );
}

function BetaApp() {
  const { t } = useTranslation("auth");
  const auth = useAuthSession();
  const access = useBetaAccess(auth.user?.id);
  const previousIdentity = useRef<typeof auth.user>(null);
  if (auth.user) previousIdentity.current = auth.user;
  if (auth.signedOut) previousIdentity.current = null;
  const identity = auth.user || previousIdentity.current;
  const account = useMemo(
    () =>
      identity && import.meta.env.VITE_SUPABASE_URL
        ? localAccount(import.meta.env.VITE_SUPABASE_URL, identity.id, identity.email || "")
        : undefined,
    [identity?.id, identity?.email],
  );
  const noticeFocused = useRef(false);
  useEffect(() => {
    if (!access.problem && noticeFocused.current) {
      noticeFocused.current = false;
      document.getElementById("main-content")?.focus();
    }
  }, [access.problem]);
  const [localPreview, setLocalPreview] = useState(false);
  const [signedOutNotice, setSignedOutNotice] = useState(false);
  const [signInRequested] = useState(new URLSearchParams(location.search).get("signin") === "1");
  useEffect(() => {
    if (auth.user?.id) {
      setSignedOutNotice(false);
      void access.check();
    } else if (auth.signedOut) {
      // Successful explicit logout also closes the recovery view in other tabs.
      access.clear();
      setSignedOutNotice(true);
    }
  }, [auth.user?.id, auth.signedOut, access.check, access.clear]);
  const canPreviewLocally = import.meta.env.DEV && ["127.0.0.1", "localhost", "::1"].includes(location.hostname);

  if (auth.loading)
    return (
      <main className="loading-screen">
        <BrandIcon className="brand-loader" />
        <p>{i18n.t("screenCopy:app.checkingSignInStatus")}</p>
      </main>
    );
  if (signInRequested || (!account && !localPreview))
    return (
      <AuthPage
        configured={auth.configured}
        signedOut={signedOutNotice}
        onGoogle={auth.signInWithGoogle}
        onLocalPreview={canPreviewLocally ? () => setLocalPreview(true) : undefined}
      />
    );

  const signOutOfDevice = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      access.clear();
      await access.check();
      throw error;
    }
    access.clear();
    setLocalPreview(false);
    setSignedOutNotice(true);
  };
  const teacher =
    account &&
    !(access.problem && access.readOnly) &&
    (DRIVE_SYNC_RELEASED || (DRIVE_SYNC_INVITE && identity?.app_metadata?.sync_v2_access === true)) ? (
      <DriveSyncWorkspace
        account={account}
        user={auth.user}
        onSignOut={signOutOfDevice}
        assertAccess={access.assertWritable}
      />
    ) : (
      <TeacherConsole
        key={account?.workspaceId || "local-preview"}
        user={auth.user}
        preview={false}
        localMode
        localAccount={account}
        assertWritable={localPreview ? undefined : access.assertWritable}
        onExitPreview={() => {
          location.href = "/";
        }}
        onSignOut={signOutOfDevice}
      />
    );

  return (
    <>
      {!localPreview && access.problem && (
        <section
          className="local-trial-banner"
          aria-label={t("accessLabel")}
          onFocus={() => {
            noticeFocused.current = true;
          }}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) noticeFocused.current = false;
          }}
        >
          <div role="status" aria-atomic="true">
            <b>{access.readOnly ? t("accessReadOnlyTitle") : t("accessConnectionTitle")}</b>{" "}
            <span>
              {access.problem === "connection" ? t("accessConnectionFailed") : access.message}{" "}
              {access.readOnly && t("accessDataRetained")}
            </span>
          </div>
          {!access.readOnly && (
            <span>{t("accessGraceRemaining", { minutes: Math.ceil(access.remainingMs / 60_000) })}</span>
          )}
          <button
            type="button"
            aria-disabled={access.checking}
            onClick={() => {
              if (!access.checking) void access.check();
            }}
          >
            {access.checking ? t("accessRetrying") : t("accessRetry")}
          </button>
          {access.problem === "sign-in-required" && (
            <a href="/app?signin=1" target="_blank" rel="noopener noreferrer">
              {t("accessSignIn")}
            </a>
          )}
          {access.problem === "update-required" && (
            // The update notice owns the download, the confirmation and the wait for pending saves.
            <button type="button" onClick={() => window.dispatchEvent(new Event(PWA_CHECK_EVENT))}>
              {t("accessUpdate")}
            </button>
          )}
        </section>
      )}
      {account ? (
        <LocalAccountGate
          key={account.workspaceId}
          account={account}
          canClaim={Boolean(auth.user) && access.verifiedUserId === account.userId && !access.readOnly}
          onSwitchAccount={async () => {
            await signOutOfDevice();
            await auth.signInWithGoogle();
          }}
        >
          {teacher}
        </LocalAccountGate>
      ) : (
        teacher
      )}
      {!localPreview && <BetaFeedback />}
    </>
  );
}

export default function App() {
  const { t: brandText, i18n } = useTranslation("brand");
  const { pathname } = useLocation();
  let content;
  if (syncReviewId()) {
    content = (
      <Suspense fallback={<main className="loading-screen" />}>
        <SyncReviewApp />
      </Suspense>
    );
  } else if (
    (DRIVE_SYNC_INVITE || DRIVE_SYNC_RELEASED) &&
    pathname === "/app" &&
    new URLSearchParams(location.search).get("oauth") === "complete"
  ) {
    content = <DriveOAuthComplete />;
  } else if (pathname === "/demo" || pathname.startsWith("/demo/")) {
    content = (
      <TeacherConsole
        user={null}
        preview
        onExitPreview={() => {
          location.href = "/";
        }}
        onSignOut={async () => {
          location.href = "/";
        }}
      />
    );
  } else if (pathname === "/app" || pathname.startsWith("/app/")) {
    content = <BetaApp />;
  } else if (pathname === "/local" || pathname.startsWith("/local/")) {
    content = ["localhost", "127.0.0.1", "::1"].includes(location.hostname) ? (
      <TeacherConsole
        user={null}
        preview={false}
        localMode
        onExitPreview={() => {
          location.href = "/";
        }}
        onSignOut={async () => {
          location.href = "/";
        }}
      />
    ) : (
      <Navigate to="/app" replace />
    );
  } else {
    content = <CloudApp />;
  }
  const workspaceRoute = /^\/(demo|app|local)(\/|$)/.test(pathname) || Boolean(syncReviewId());
  useEffect(() => {
    if (workspaceRoute) document.title = brandText(i18n.resolvedLanguage === "en" ? "english" : "name");
  }, [workspaceRoute, brandText, i18n.resolvedLanguage]);
  return workspaceRoute ? (
    <DesktopViewportGate>
      <PwaUpdateNotice />
      {content}
    </DesktopViewportGate>
  ) : (
    <>
      <PwaUpdateNotice />
      {content}
    </>
  );
}
